import type { LbPost, LbPart, LbPostEvent, LbOptions } from '@uefacom-newdev/liveblog-integration-react';
import {
  useOptions,
  getPostTypes,
  PartType,
  isMatchEvent,
  PartsRenderer,
  LbEventType,
  LbEventSubType,
} from '@uefacom-newdev/liveblog-integration-react';
import { PkBox } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useMemo, useCallback, memo, lazy } from 'react';

import EventPlayer from '.././EventPlayer';
import Header from '.././Header';
import Reporter from '.././Reporter';
import type { LiveblogData } from '../utils';
import { getPostHomeAway, getPostReporter } from '../utils';

import { mapEditorialContent } from '@cobalt/utilities';
import { useWindowSize } from '@hooks/useWindowSize';
import { useMediaAssetService } from '@shared/VideoPlayer/hooks/useMediaAssetService';
import type { Draw } from '@shared-types/draw.types';
import type { Match, PlayerPosition, Team } from '@shared-types/match.types';
import { isEmpty } from '@utilities/collection-utils';
import { dateToString } from '@utilities/dateTime';

import * as classes from './styles.module.scss';

const Gallery = lazy(() => import(/* webpackPrefetch: true */ '../../liveblog/Gallery'));
const Reactions = lazy(() => import(/* webpackPrefetch: true */ '../../liveblog/Reactions'));

const boldEventTypes: LbEventType[] = [
  LbEventType.START_PHASE,
  LbEventType.END_PHASE,
  LbEventType.PAUSE,
  LbEventType.RESUME,
  LbEventType.CHANGE_PHASE,
  LbEventType.INJURY_TIME,
];
const enrichedEventTypes: LbEventType[] = [
  LbEventType.YELLOW_CARD,
  LbEventType.RED_CARD,
  LbEventType.GOAL,
  LbEventType.SUBSTITUTION,
  LbEventType.PENALTY,
];

const Container = ({ post, post: { parts, lastUpdateDate, liveblogPostData } }: { post: LbPost }) => {
  const {
    match,
    liveblogData,
    draw,
    disablePlayerLink,
    includeReactions,
  }: LbOptions & {
    match: Match;
    liveblogData: LiveblogData;
    draw: Draw;
    disablePlayerLink: boolean;
  } = useOptions() as LbOptions & {
    match: Match;
    liveblogData: LiveblogData;
    draw: Draw;
    disablePlayerLink: boolean;
  };

  const { isMobile } = useWindowSize();

  const event = liveblogPostData?.lbPostFSPEvent ?? ({} as LbPostEvent);
  const postTypes = getPostTypes(parts, liveblogPostData);
  const hasEvent = isMatchEvent(liveblogPostData);
  const hasGallery = parts.filter((part) => part.datasource === PartType.FREE_PHOTO).length > 1;
  const postReporter = getPostReporter(post, liveblogData);
  const homeAway = getPostHomeAway(match, post.liveblogPostData, postReporter);
  const isBoldEvent = boldEventTypes.includes(event.eventType);
  const isEnrichedEvent = enrichedEventTypes.includes(event.eventType) && event.namePlayer;
  const isGoalEvent =
    event.eventType === LbEventType.GOAL ||
    (event.eventType === LbEventType.PENALTY && event.eventSubType === LbEventSubType.SCORED);
  const isSubstitutionEvent = event.eventType === LbEventType.SUBSTITUTION;

  const teamType: Team | 'neutral' =
    {
      home: match?.homeTeam,
      away: match?.awayTeam,
    }[homeAway] ?? 'neutral';
  const typesClass = useMemo(() => postTypes.map((t) => classes[t.toLowerCase()]).join(' '), [postTypes]);
  const isSocial = useMemo(() => postTypes.includes(PartType.SOCIAL), [postTypes]);
  const socialTypesClass = useMemo(
    () =>
      parts
        .filter((p) => p.datasource === PartType.SOCIAL)
        .map((p) => classes[`social--${p.data.Type.toLowerCase()}`])
        .join(' '),
    [parts],
  );

  const filterFirstLineIfDuplicated = useCallback(
    (part: LbPart, index: number, firstTextElement: number) => {
      if (![PartType.COMPOSER, PartType.MATCH_EVENTS].includes(part.datasource) || index > firstTextElement)
        return true;
      if (isEnrichedEvent) return !(isSubstitutionEvent || isGoalEvent);
      return !isBoldEvent;
    },
    [isBoldEvent, isEnrichedEvent, isGoalEvent, isSubstitutionEvent],
  );

  const pollPart = useMemo(() => parts.find((part) => part.datasource === PartType.POLL), [parts]);
  const galleryParts = useMemo(
    () => hasGallery && parts.filter((part) => part.datasource === PartType.FREE_PHOTO),
    [hasGallery, parts],
  );

  const firstTextIndexBeforeFilter = parts.findIndex((part) =>
    [PartType.COMPOSER, PartType.MATCH_EVENTS].includes(part.datasource),
  );

  const additionalTime = useMemo(() => {
    if (!draw) return false;

    return (
      !hasGallery &&
      parts.length > 1 &&
      parts.some((part) => [PartType.FREE_PHOTO, PartType.POLL, PartType.UEFA_EDITORIAL].includes(part.datasource))
    );
  }, [draw, hasGallery, parts]);

  const hideTime = useMemo(() => {
    if (!draw) return false;
    if (additionalTime) return true;
    if (hasGallery) return true;

    return (
      parts.length <= 1 &&
      parts.some((part) =>
        [PartType.FREE_PHOTO, PartType.POLL, PartType.UEFA_EDITORIAL, PartType.SOCIAL].includes(part.datasource),
      )
    );
  }, [additionalTime, draw, parts, hasGallery]);

  const videos = useMemo(
    () =>
      parts
        .filter((part) => part.datasource === PartType.UEFA_EDITORIAL && part.data.MediaType === 'video')
        .map(({ data }) => mapEditorialContent(data.Editorial)),
    [parts],
  );
  const availableVideos = useMediaAssetService(videos);

  const filteredParts = useMemo(
    () =>
      parts.filter(
        (part, index) =>
          part.datasource !== PartType.POLL &&
          (!hasGallery || part.datasource !== PartType.FREE_PHOTO) &&
          (!hasEvent || filterFirstLineIfDuplicated(part, index, firstTextIndexBeforeFilter)) &&
          (part.datasource !== PartType.UEFA_EDITORIAL ||
            part.data?.MediaType !== 'video' ||
            availableVideos.find((v) => v.id === part.data?.Id)),
      ),
    [parts, hasGallery, hasEvent, filterFirstLineIfDuplicated, firstTextIndexBeforeFilter, availableVideos],
  );

  const firstTextIndex = useMemo(
    () => filteredParts.findIndex((part) => [PartType.COMPOSER, PartType.MATCH_EVENTS].includes(part.datasource)),
    [filteredParts],
  );
  const hasText = useMemo(() => firstTextIndex > -1, [firstTextIndex]);

  const partsAboveEvent = useMemo(() => {
    if (!hasEvent) return [];
    if (!hasText) return filteredParts;
    return filteredParts.slice(0, firstTextIndex);
  }, [firstTextIndex, hasText, hasEvent, filteredParts]);

  const partsMiddleEvent = useMemo(() => {
    if (!hasEvent) {
      if (!additionalTime) return filteredParts;

      const dTimePart: LbPart = {
        datasource: PartType.COMPOSER,
        data: {
          Text: `<span class=${classes.drawTime}>${dateToString(lastUpdateDate, 'HH:mm')}</span>`,
        },
      };
      const filteredPartsWithTime = [...filteredParts];
      filteredPartsWithTime.splice(firstTextIndex > 1 ? 0 : firstTextIndex, 0, dTimePart);
      return filteredPartsWithTime;
    }
    if (!hasText) return [];
    return [filteredParts[firstTextIndex]];
  }, [additionalTime, filteredParts, firstTextIndex, hasEvent, hasText, lastUpdateDate]);

  const partsBelowEvent = useMemo(() => {
    if (!hasEvent) return [];
    if (!hasText) return [];
    return filteredParts.slice(firstTextIndex + 1, filteredParts.length);
  }, [firstTextIndex, hasText, hasEvent, filteredParts]);

  const showReactions = useMemo(
    () =>
      includeReactions &&
      !pollPart &&
      (hasGallery ||
        postTypes.includes(PartType.COMPOSER) ||
        postTypes.includes(PartType.FREE_PHOTO) ||
        postReporter ||
        isGoalEvent),
    [hasGallery, includeReactions, isGoalEvent, pollPart, postReporter, postTypes],
  );

  const reactionPostType = useMemo(() => {
    if (isGoalEvent) return 'goal';

    if (postReporter?.type) {
      return postReporter?.type === 'star' ? 'star reporter' : 'reporter';
    }

    if (postTypes.includes(PartType.COMPOSER)) {
      return postTypes.every((t) => t === PartType.COMPOSER) ? 'text only' : 'composed';
    }

    if (hasGallery) return 'photo gallery';
    if (postTypes.includes(PartType.FREE_PHOTO)) return 'image';
    if (postTypes.includes(PartType.VIDEO_POST)) return 'video';

    return 'text only';
  }, [hasGallery, isGoalEvent, postReporter?.type, postTypes]);

  // if there are no parts, we don't want to show the post
  if (
    !hasEvent &&
    !partsAboveEvent.length &&
    !partsMiddleEvent.length &&
    !partsBelowEvent.length &&
    !pollPart &&
    !galleryParts.length
  ) {
    return null;
  }

  return (
    <>
      {/*"lb-goal-event" class handle the style for Goal cards during regular time or during Penalty shootout */}
      {event.eventType === LbEventType.END_PHASE && <span className={classes.dividerElement} />}
      <PkBox
        key={`post_${post.id}`}
        pk-theme={isGoalEvent ? 'dark' : 'light'}
        stroke={!(isSocial || (isGoalEvent && !isMobile))}
        paddingNone
        className={clsx({
          [classes.post]: true,
          'is-draw': !isEmpty(draw),
          [socialTypesClass]: isSocial,
          [classes.withAuthor]: !!postReporter?.type,
          [classes.postWithStarAuthor]: postReporter?.type === 'star',
          [typesClass]: typesClass,
          [classes.goalEvent]: isGoalEvent,
          [classes.varEvent]: event.eventType === LbEventType.VAR || event.varInfo,
          [classes.postNeutral]: homeAway === 'neutral',
        })}
      >
        <PartsRenderer post={post} parts={partsAboveEvent} />
        {!hideTime && <Header liveblogPostData={liveblogPostData} date={lastUpdateDate} />}

        {postReporter && <Reporter reporter={postReporter} team={teamType} />}
        <PartsRenderer post={post} parts={partsMiddleEvent} />
        {!!isEnrichedEvent && (
          <div className={classes.matcheventContainer}>
            {!!event.idPlayer && (
              <EventPlayer
                id={event.idPlayer}
                name={event.namePlayer}
                fieldPosition={event.fieldPositionPlayer as PlayerPosition}
                imageUrl={event.imageUrlPlayer}
                isGoalEvent={isGoalEvent}
                substitutionStatus={isSubstitutionEvent ? 'out' : null}
                enableLink={!disablePlayerLink}
                phase={match?.round?.phase}
              />
            )}
            {isSubstitutionEvent && !!event.idPlayerTo && (
              <EventPlayer
                id={event.idPlayerTo}
                name={event.namePlayerTo}
                fieldPosition={event.fieldPositionPlayerTo as PlayerPosition}
                imageUrl={event.imageUrlPlayerTo}
                isGoalEvent={isGoalEvent}
                substitutionStatus={isSubstitutionEvent ? 'in' : null}
                enableLink={!disablePlayerLink}
                phase={match?.round?.phase}
              />
            )}
          </div>
        )}
        <PartsRenderer post={post} parts={partsBelowEvent} />
        {pollPart && <PartsRenderer post={post} parts={[pollPart]} />}
        {galleryParts && <Gallery post={post} parts={galleryParts} />}
        {showReactions && <Reactions post={post} postType={reactionPostType} />}
      </PkBox>
    </>
  );
};

export default memo(Container);
